import { FormEvent, useEffect, useRef, useState } from "react";
import type { Contact, DocumentVault } from "../../../types/TradeFlow";
import { useGetTradeFlowByIdQuery } from "../../../store/apiSlices/tradeFlow";
import { BellRingIcon, CrossIcon, UploadIcon } from "../../Icons";
import CustomModal from "../CustomModal";
import NotifyContacts from "./NotifyContacts";
import UploadDocVault from "./UploadDocVault";
import { useTradeFlowTracker } from "../../../context/TradeFlowTrackerContext";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";

// import NotifyAndDocUpload from "./NotifyAndDocUpload";

const initialDocument = {
  document_name: "",
};

export default function DocumentVault() {
  const { isViewer } = useTradeFlowTracker();
  const { tradeFlowData, updateTradeFlow } = useTradeFlowData();
  const { trade_flow_json, id, row_id } = tradeFlowData;

  const handleAddDocumentVault = async (
    step_id: string,
    doc: DocumentVault
  ) => {
    if (isViewer) return;

    const updatedSteps = trade_flow_json.map((step) =>
      step.step_id === step_id
        ? {
            ...step,
            document_vault: [...step.document_vault, doc],
          }
        : step
    );

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  const handleSaveDocument = async (
    step_id: string,
    document_id: string,
    updatedDocumentVault: DocumentVault
  ) => {
    const updatedSteps = trade_flow_json.map((step) => {
      if (step.step_id === step_id) {
        const updatedDocumentVaults = step.document_vault.map((doc) =>
          doc.document_id === document_id ? updatedDocumentVault : doc
        );

        return {
          ...step,
          document_vault: updatedDocumentVaults,
        };
      }
      return step;
    });

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  const handleRemoveDocument = async (step_id: string, document_id: string) => {
    const updatedSteps = trade_flow_json.map((step) =>
      step.step_id === step_id
        ? {
            ...step,
            document_vault: step.document_vault.filter(
              (doc) => doc.document_id !== document_id
            ),
          }
        : step
    );

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  return (
    <section className="w-full flex justify-start items-start gap-3 text-xs h-full overflow-auto will-change-transform">
      {trade_flow_json.map((el, idx) => (
        <div
          key={el.step_id}
          className="relative min-w-[200px] max-w-[150px] flex-shrink-0"
        >
          <div className="w-full h-[100px] flex flex-col items-center justify-start text-center text-gray-900 gap-2">
            <h4 className="size-8 text-lg bg-white z-10 flex items-center justify-center p-1 border-2 border-gray-900 rounded-full">
              {idx + 1}
            </h4>
            <p className="font-medium">{el.step.field}</p>
          </div>

          {idx < trade_flow_json.length - 1 && (
            <div className="absolute top-4 right-[-100px] w-full h-[1px] bg-gray-900" />
          )}
          {idx !== 0 && (
            <div className="absolute top-4 left-[-100px] w-full h-[1px] bg-gray-900" />
          )}

          <div className="space-y-2">
            {el.document_vault.map((doc, i) => (
              <div
                key={doc.document_id || i}
                className="relative p-2 text-gray-500 space-y-3 border shadow-sm rounded-xl"
              >
                {!isViewer && (
                  <button
                    type="button"
                    className="absolute -top-[2px] -right-[4px] p-[1px] bg-gray-300 rounded-full hover:bg-red-500 hover:text-white hover:scale-90"
                    onClick={() =>
                      handleRemoveDocument(el.step_id!, doc.document_id!)
                    }
                  >
                    <CrossIcon className="size-4" />
                  </button>
                )}
                <EditableDocumentForm
                  documentVault={doc}
                  contacts={el.Contacts}
                  row_id={row_id}
                  step_id={el.step_id!}
                  handleSaveDocument={handleSaveDocument}
                  isViewer={isViewer}
                />
              </div>
            ))}

            <button
              disabled={isViewer}
              title={isViewer ? "You don’t have permission to edit" : undefined}
              className="
                w-full px-2 py-1 font-medium rounded-full border border-dashed
                bg-gray-100 text-gray-700
                disabled:bg-gray-200
                disabled:text-gray-400
                disabled:cursor-not-allowed
              "
              onClick={() =>
                handleAddDocumentVault(el.step_id!, initialDocument)
              }
            >
              + Add Document
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

type Props = {
  documentVault: DocumentVault;
  contacts: Contact[];
  row_id: string;
  step_id: string;
  handleSaveDocument: (
    step_id: string,
    document_id: string,
    updatedDocumentVault: DocumentVault
  ) => void;
  isViewer: boolean;
};

type FieldKey = "document_name" | "person";

function EditableDocumentForm({
  documentVault,
  contacts,
  row_id,
  step_id,
  handleSaveDocument,
  isViewer,
}: Props) {
  const [isEditable, setIsEditable] = useState(false);
  const [focusField, setFocusField] = useState<FieldKey | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalType, setModalType] = useState<"notify" | "upload">();

  const formRef = useRef<HTMLFormElement>(null);

  const { refetch } = useGetTradeFlowByIdQuery(row_id, {
    skip: !row_id,
  });

  useEffect(() => {
    if (isEditable && focusField && formRef.current) {
      const input = formRef.current.querySelector<HTMLInputElement>(
        `input[name="${focusField}"]`
      );

      input?.focus();
    }
  }, [isEditable, focusField]);

  const toggleModal = () => {
    if (isViewer) return;

    setIsModalOpen((prev) => !prev);
  };

  const handleEdit = (field: FieldKey) => {
    if (isViewer) return;

    setFocusField(field);
    setIsEditable(true);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const raw = Object.fromEntries(formData.entries());

    const values: DocumentVault = {
      document_name: (raw.document_name as string).trim(),
    };

    handleSaveDocument(step_id, documentVault.document_id!, values);
    setIsEditable(false);
  };

  return isEditable ? (
    <form className="w-full space-y-3" ref={formRef} onSubmit={handleSubmit}>
      <input
        className="w-full outline-none"
        type="text"
        name="document_name"
        placeholder="Enter doc name"
        defaultValue={documentVault.document_name}
        required
        minLength={2}
      />

      <button
        type="submit"
        className="w-full py-1 bg-custom-blue-medium text-white rounded-xl"
        id="save-document-vault-name-btn"
      >
        save
      </button>
    </form>
  ) : (
    <div>
      <p
        onClick={() => handleEdit("document_name")}
        className={
          documentVault.document_name ? "font-medium text-gray-800" : ""
        }
      >
        {documentVault.document_name || "Enter doc name"}
      </p>
      {documentVault.document_name && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="hover:scale-95 hover:text-custom-blue-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            onClick={() => {
              setModalType("notify");
              toggleModal();
            }}
            disabled={isViewer}
          >
            <BellRingIcon className="size-6" />
          </button>
          <button
            type="button"
            className="hover:scale-95 disabled:text-gray-400 disabled:cursor-not-allowed"
            onClick={() => {
              setModalType("upload");
              refetch();
              toggleModal();
            }}
            disabled={isViewer}
          >
            <UploadIcon className="size-10" />
          </button>
        </div>
      )}

      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
      >
        <button
          onClick={toggleModal}
          className="float-end hover:scale-95 hover:text-red-400"
        >
          <CrossIcon className="size-6" />
        </button>

        {modalType && modalType === "notify" && (
          <NotifyContacts
            toggleModal={toggleModal}
            contacts={contacts}
            documentVault={documentVault}
            row_id={row_id}
            step_id={step_id}
          />
        )}
        {modalType && modalType === "upload" && (
          <UploadDocVault
            toggleModal={toggleModal}
            documentVault={documentVault}
            row_id={row_id}
            step_id={step_id}
          />
        )}
      </CustomModal>
    </div>
  );
}
